import React, { useEffect, useState } from "react";
import { imgSrc, imgSet } from "@/lib/img";
import { Link, useParams } from "react-router-dom";
import { MapPin, Compass, ArrowRight, ArrowLeft } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import JsonLd from "@/components/site/JsonLd";
import ExpeditionCard from "@/components/site/ExpeditionCard";
import NotFound from "@/pages/NotFound";
import { useLang } from "@/lib/i18n";
import { L } from "@/lib/localize";
import { useSeo } from "@/lib/seo";
import { graph, breadcrumbNode, parseCoordinates } from "@/lib/schema";
import { SITE_URL } from "@/lib/seo";
import { useAssets, tourCover, leaderPortrait } from "@/lib/assets";
import api from "@/lib/api";

export default function DestinationDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const assets = useAssets();
  const [dest, setDest] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setDest(null);
    setNotFound(false);
    api.get(`/destinations/${slug}`).then((r) => setDest(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const firstTour = dest && dest.tours[0] ? L(dest.tours[0], lang) : null;
  const region = firstTour ? firstTour.location || dest.region : "";
  const country = firstTour ? firstTour.country || dest.country : "";

  useSeo({
    title: dest ? `${dest.name}${country ? `, ${country}` : ""} — ${t("dest.seoSuffix")}` : t("seo.dest.title"),
    description: firstTour ? (firstTour.excerpt || "").replace(/\s+/g, " ").slice(0, 157) : t("seo.dest.desc"),
    image: dest && dest.tours[0] ? tourCover(assets, dest.tours[0]) : undefined,
    path: `/destinazioni/${slug}`,
    noindex: notFound,
  });

  if (notFound) return <NotFound />;

  const geo = dest ? parseCoordinates(dest.coordinates) : undefined;
  const jsonLd = dest
    ? graph([
        {
          "@type": "TouristDestination",
          "@id": `${SITE_URL}${lang === "it" ? "" : `/${lang}`}/destinazioni/${slug}#destination`,
          name: dest.name,
          description: firstTour ? (firstTour.excerpt || "").slice(0, 300) : undefined,
          address: { "@type": "PostalAddress", addressRegion: region, addressCountry: country },
          ...(geo ? { geo } : {}),
          includesAttraction: dest.tours.map((tr) => ({
            "@type": "TouristAttraction",
            name: L(tr, lang).title,
            url: `${SITE_URL}${lang === "it" ? "" : `/${lang}`}/viaggi/${tr.slug}`,
          })),
        },
        breadcrumbNode(
          [
            { name: "Home", path: "/" },
            { name: t("nav.destinations"), path: "/destinazioni" },
            { name: dest.name, path: `/destinazioni/${slug}` },
          ],
          lang
        ),
      ])
    : null;

  return (
    <div className="bg-obsidian text-cream min-h-screen" data-testid="destination-page">
      <Nav />
      <JsonLd id="jsonld-destination" data={jsonLd} />

      <section className="pt-32 sm:pt-44 pb-10">
        <div className="container-x">
          <Link to="/destinazioni" className="font-mono text-[11px] uppercase tracking-[0.22em] text-copper inline-flex items-center gap-2 mb-8" data-testid="destination-back">
            <ArrowLeft className="h-4 w-4" /> {t("nav.destinations")}
          </Link>
          {dest && (
            <Reveal>
              <p className="eyebrow mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4" /> {region ? `${region} · ` : ""}{country}
              </p>
              <h1 className="font-serif font-semibold text-5xl sm:text-7xl leading-[0.95]" data-testid="destination-name">{dest.name}</h1>
              {firstTour && <p className="mt-6 max-w-2xl text-cream2 leading-relaxed">{firstTour.excerpt}</p>}
              {dest.coordinates && (
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-sand flex items-center gap-2">
                  <Compass className="h-3.5 w-3.5 text-copper" /> {dest.coordinates}
                </p>
              )}
            </Reveal>
          )}
        </div>
      </section>

      {dest && (
        <section className="pb-20 sm:pb-28" data-testid="destination-tours">
          <div className="container-x">
            <Reveal>
              <p className="eyebrow mb-8">{t("dest.tripsHere")}</p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
              {dest.tours.map((tr, i) => (
                <ExpeditionCard key={tr.id} tour={tr} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {dest && dest.leaders.length > 0 && (
        <section className="pb-20 sm:pb-28" data-testid="destination-leaders">
          <div className="container-x">
            <Reveal>
              <p className="eyebrow mb-8">{t("dest.leadersHere")}</p>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {dest.leaders.map((l) => {
                const ll = L(l, lang);
                const portrait = leaderPortrait(assets, ll);
                return (
                  <Link key={l.id} to={`/leader/${l.slug}`} className="group block" data-testid={`destination-leader-${l.slug}`}>
                    <div className="aspect-[3/4] overflow-hidden border border-white/5 group-hover:border-copper/40 transition-colors">
                      {portrait && (
                        <img
                          src={imgSrc(portrait, 400)}
                          srcSet={imgSet(portrait, [400, 800])}
                          sizes="(max-width: 1024px) 50vw, 25vw"
                          alt={ll.name}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[1200ms]"
                        />
                      )}
                    </div>
                    <p className="font-serif text-xl mt-4 group-hover:text-copperlight transition-colors">{ll.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand mt-1">{ll.role}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {dest && (
        <section className="pb-24 sm:pb-32">
          <div className="container-x border-t border-white/5 pt-10 flex flex-wrap gap-4">
            <Link to="/viaggi" className="btn-ghost" data-testid="destination-all-tours">
              {t("home.exped.cta")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contatti" className="btn-copper" data-testid="destination-contact">
              {t("nav.contact")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
